import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check, X, Clock, SlidersHorizontal, Loader2, UserRound, MessageSquare, CalendarClock, ListChecks, Bot, FileSignature } from "lucide-react";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";

export const TYPE_ICON = {
  client_contact: UserRound, unanswered_message: MessageSquare, meeting_prep: CalendarClock,
  program_week: ListChecks, agenda_proposal: Bot, contract_expiry: FileSignature,
};
export const TYPE_LABEL_NL = {
  client_contact: "Clientcontact", unanswered_message: "Onbeantwoord", meeting_prep: "Voorbereiding",
  program_week: "Weekprogramma", agenda_proposal: "Agenda", contract_expiry: "Contract",
};

function scoreTone(score) {
  if (score >= 0.8) return "border-red-200 bg-red-50/50";
  if (score >= 0.6) return "border-amber-200 bg-amber-50/40";
  return "border-slate-200 bg-white";
}

export default function SignalCard({ signal, onFeedback, compact = false }) {
  const t = useT();
  const nav = useNavigate();
  const [busy, setBusy] = useState(null);
  const [adjusting, setAdjusting] = useState(false);
  const [threshold, setThreshold] = useState(signal.days_since ? signal.days_since + 7 : 21);
  const Icon = TYPE_ICON[signal.type] || Bot;

  const send = async (action, extra = {}) => {
    setBusy(action);
    try { await api.post(`/assistant/signals/${signal.id}/feedback`, { action, ...extra }); onFeedback(signal, action); }
    finally { setBusy(null); }
  };

  const act = async () => {
    await send("acted");
    nav(signal.action_route);
  };

  return (
    <div className={`rounded-xl border p-4 transition-shadow hover:shadow-md ${scoreTone(signal.score)}`} data-testid={`signal-${signal.id}`} data-signal-type={signal.type}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-700 shrink-0"><Icon size={15} /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">{t(TYPE_LABEL_NL[signal.type] || signal.type_label)}</span>
            <span className="text-[10px] font-mono-tab text-slate-400" title={t("prioriteitsscore × geleerd gewicht")} data-testid="signal-score">score {signal.score.toFixed(2)}{signal.weight && signal.weight !== 1 ? ` · w${signal.weight.toFixed(2)}` : ""}</span>
          </div>
          <div className="font-semibold text-slate-900 text-sm mt-0.5" data-testid="signal-title">{t(signal.title)}</div>
          {!compact && <div className="text-xs text-slate-600 mt-1" data-testid="signal-detail">{t(signal.detail)}</div>}

          {adjusting && (
            <div className="mt-3 flex items-center gap-2 text-xs" data-testid="signal-adjust-form">
              <span>{t("Drempel voor dit type:")}</span>
              <input type="number" min="1" max="365" className="pm-input h-8 w-20" value={threshold} onChange={(e) => setThreshold(Number(e.target.value))} data-testid="signal-adjust-input" />
              <span>{t("dagen")}</span>
              <button onClick={() => send("adjusted", { threshold_days: threshold })} className="pm-btn-primary h-8 px-3 rounded-lg text-xs font-bold" data-testid="signal-adjust-save">{t("Opslaan")}</button>
              <button onClick={() => setAdjusting(false)} className="text-slate-500" data-testid="signal-adjust-cancel">{t("annuleer")}</button>
            </div>
          )}

          <div className="flex items-center gap-1.5 mt-3 flex-wrap">
            <button onClick={act} disabled={!!busy} className="pm-btn-primary h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1" data-testid="signal-act">
              {busy === "acted" ? <Loader2 size={12} className="animate-spin" /> : <ArrowRight size={12} />} {t(signal.action_label)}
            </button>
            <button onClick={() => send("acted")} disabled={!!busy} title={t("Al gedaan")} className="pm-btn-secondary h-8 px-2.5 rounded-lg text-xs font-bold flex items-center gap-1" data-testid="signal-done"><Check size={12} /> {t("Gedaan")}</button>
            <button onClick={() => send("snoozed", { snooze_days: 3 })} disabled={!!busy} className="pm-btn-secondary h-8 px-2.5 rounded-lg text-xs font-bold flex items-center gap-1" data-testid="signal-snooze"><Clock size={12} /> 3d</button>
            {!compact && (signal.type === "client_contact" || signal.type === "contract_expiry") && (
              <button onClick={() => setAdjusting((v) => !v)} className="pm-btn-secondary h-8 px-2.5 rounded-lg text-xs font-bold flex items-center gap-1" data-testid="signal-adjust"><SlidersHorizontal size={12} /> {t("Drempel")}</button>
            )}
            <button onClick={() => send("dismissed", { reason: "not_relevant" })} disabled={!!busy} className="pm-btn-secondary h-8 px-2.5 rounded-lg text-xs font-bold text-slate-500 flex items-center gap-1 ml-auto" data-testid="signal-dismiss"><X size={12} /> {t("Niet relevant")}</button>
            {!compact && signal.days_since != null && signal.type === "client_contact" && (
              <button onClick={() => send("dismissed", { reason: "too_early" })} disabled={!!busy} className="pm-btn-secondary h-8 px-2.5 rounded-lg text-xs font-bold text-slate-500" data-testid="signal-too-early">{t("Te vroeg")}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
